import { useContext, useEffect, useMemo, useState } from 'react';
import { Layout, Badge, Button, Tooltip, Dropdown, Space, Typography } from 'antd';
import {
  BellOutlined,
  SunOutlined,
  MoonOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@hooks/useTheme';
import { THEME_MODE_LABELS, THEME_TOGGLE_PAIR } from '@config/theme.utils';
import { useAppSelector, useAppDispatch } from '@store/hooks';
import { setSidebarCollapsed } from '@store/slices/appSlice';
import {
  fetchNotifications,
  fetchUnreadCount,
  markAllAsReadAsync,
  markReadAsync,
} from '@store/slices/notificationSlice';
import { AppContext } from '@context/AppContext';
import { SessionTimeoutBar } from './SessionTimeoutBar';

const { Header: AntHeader } = Layout;
const { Title } = Typography;

/** Polling interval for the unread badge — fetched on mount (page refresh) and every 10 minutes. */
const UNREAD_POLL_INTERVAL_MS = 10 * 60 * 1_000;

function formatRelativeTime(ts: number): string {
  const diffMs = Date.now() - ts;
  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

export function Header() {
  const { mode, toggleTheme, isDark } = useTheme();
  const nextModeLabel = THEME_MODE_LABELS[THEME_TOGGLE_PAIR[mode]];
  const { pageTitle } = useContext(AppContext);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const collapsed = useAppSelector((state) => state.app.sidebarCollapsed);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const unreadCount = useAppSelector((state) => state.notifications.unreadCount);
  // Select the raw items reference (stable across renders unless the
  // slice actually mutates) and derive the head slice in a memo. Doing
  // ``.slice(0, 5)`` inside the selector returns a fresh array every
  // render, which trips React Redux's "selector returned a different
  // result with the same parameters" warning.
  const allNotifications = useAppSelector((state) => state.notifications.items);
  const notifications = useMemo(() => allNotifications.slice(0, 5), [allNotifications]);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Fetch unread count on mount (page refresh) and then every 10 minutes.
  // The list itself is only refetched when the bell dropdown opens.
  useEffect(() => {
    if (!isAuthenticated) return;
    void dispatch(fetchUnreadCount());
    const id = window.setInterval(() => {
      void dispatch(fetchUnreadCount());
    }, UNREAD_POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [dispatch, isAuthenticated]);

  // Reload the full list (and sync the unread count) when the bell
  // dropdown opens so the user always sees the latest titles.
  useEffect(() => {
    if (dropdownOpen && isAuthenticated) {
      void dispatch(fetchNotifications({ limit: 20 }));
    }
  }, [dispatch, dropdownOpen, isAuthenticated]);

  const handleNotificationClick = (id: string, link: string | null | undefined) => {
    void dispatch(markReadAsync(id));
    setDropdownOpen(false);
    if (link) {
      navigate(link);
    }
  };

  const notificationItems: MenuProps['items'] = notifications.length > 0
    ? [
        ...notifications.map((n) => ({
          key: n.id,
          onClick: () => handleNotificationClick(n.id, n.link),
          label: (
            <div style={{ maxWidth: 320 }}>
              <div style={{ fontWeight: n.read ? 'normal' : 600, fontSize: 13 }}>{n.message}</div>
              {n.description && (
                <div
                  style={{
                    fontSize: 11,
                    opacity: 0.7,
                    marginTop: 2,
                    whiteSpace: 'normal',
                    lineHeight: 1.4,
                  }}
                >
                  {n.description}
                </div>
              )}
              <div style={{ fontSize: 10, opacity: 0.5, marginTop: 4 }}>
                {formatRelativeTime(n.timestamp)}
              </div>
            </div>
          ),
        })),
        { type: 'divider' as const },
        {
          key: 'mark-all',
          label: 'Mark all as read',
          onClick: () => {
            void dispatch(markAllAsReadAsync());
          },
        },
      ]
    : [{ key: 'empty', label: 'No notifications', disabled: true }];

  return (
    <AntHeader
      style={{
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
        position: 'sticky',
        top: 0,
        zIndex: 10,
        height: 64,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <Button
          type="text"
          icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={() => dispatch(setSidebarCollapsed(!collapsed))}
        />
        <Title level={4} style={{ margin: 0 }}>
          {pageTitle}
        </Title>
      </div>

      <Space size="middle">
        <SessionTimeoutBar />

        {/*
          Mount point for the minimized chat assistant dot. Rendered via
          React Portal from ``ClippyAssistant`` when ``chatMinimized`` is
          true. Sits between the session bar and the theme toggle so it
          claims its own slot in the header instead of overlapping the
          theme toggle (the previous ``position: fixed; right: 80px``
          placement landed exactly on top of the Sun/Moon button).
        */}
        <span id="chat-minimized-slot" style={{ display: 'inline-flex', alignItems: 'center' }} />

        <Tooltip title={`Switch to ${nextModeLabel}`}>
          <Button
            type="text"
            icon={isDark ? <SunOutlined /> : <MoonOutlined />}
            onClick={toggleTheme}
          />
        </Tooltip>

        <Dropdown
          menu={{ items: notificationItems }}
          trigger={['click']}
          placement="bottomRight"
          open={dropdownOpen}
          onOpenChange={setDropdownOpen}
        >
          <Badge count={unreadCount} size="small" offset={[-2, 2]}>
            <Button type="text" icon={<BellOutlined />} />
          </Badge>
        </Dropdown>
      </Space>
    </AntHeader>
  );
}
